/**
 * The tab's half of the audio companion channel: a socket that connects, proves who it is, keeps a
 * clock, and survives a sidecar restart.
 *
 * NO AUDIO IN THIS FILE. There is no `AudioContext`, no track, no area and no volume here — the channel
 * is only the channel. {@link AudioChannelEvents.onMessage} hands every §9.3 frame (`catalog`,
 * `context`, `cue`) to the page, which feeds the engine (`engine.ts`).
 *
 * NO REACT EITHER. This is a plain class with injectable everything, because the interesting
 * behaviour is a reconnect schedule and a clock estimate, and neither is easier to assert through a
 * render. A hook that wraps it is a later, smaller thing.
 *
 * ---
 *
 * WHAT THE PHASE 0 SPIKE FORCED ON THE DESIGN, because two of these decisions look arbitrary
 * otherwise:
 *
 * 1. **The clock probe is on a `setInterval`, and that is knowingly unreliable in a hidden tab.**
 *    The spike measured a hidden tab that is PLAYING AUDIO holding 4 Hz timers, and a hidden tab
 *    that is SILENT falling to 0.01 Hz. Since the probe exists to keep the offset fresh for
 *    seeking, and seeking only matters while something is playing, the throttled case is the case
 *    where the answer is not needed. What must NOT depend on this timer is liveness — and it does
 *    not: the sidecar pings at the protocol level and the browser answers from its network stack,
 *    with no JavaScript involved. A heartbeat built on this interval would have killed exactly the
 *    socket of a player who had not pressed "enable audio" yet.
 * 2. **`offsetMs` is a signed number and never a boolean.** The spike found drift monotonic and
 *    one-directional — audio always *behind* the server — so the correction a later phase applies
 *    is a proportional `playbackRate` nudge with a hard re-seek only past a threshold. You cannot
 *    nudge a rate towards `true`, so the API exposes the magnitude. See `AudioClock`.
 */
import {
  AUDIO_PATH,
  AUDIO_PING_INTERVAL_MS,
  AudioClock,
  audioRetryDelayMs,
  audioTokenSubprotocol,
  audioVersionSubprotocol,
  isAudioServerMessage,
  type AudioRefusalCode,
  type AudioServerMessage,
  type UuidText,
} from '@dimensionscore/shared';

/** Where the channel is. `waiting` is "disconnected, and a retry is scheduled". */
export type AudioChannelStatus = 'idle' | 'connecting' | 'open' | 'waiting' | 'stopped';

/**
 * The subset of the browser's `WebSocket` this file uses.
 *
 * Narrow so a test supplies a plain object rather than standing up a server, and so the handler
 * properties (`onopen`…) are the contract rather than `addEventListener`, which would let a test
 * quietly diverge on capture and bubbling for no gain.
 */
export interface AudioSocketLike {
  /** The negotiated subprotocol. The cheapest confirmation the sidecar agreed on a generation. */
  readonly protocol?: string;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: (() => void) | null;
}

export interface AudioChannelEvents {
  /** Every status change, with a sentence for the ones that have a reason. */
  readonly onStatus?: (status: AudioChannelStatus, detail?: string) => void;
  /**
   * A hello was accepted, and this is the player the socket is bound to.
   *
   * It comes off the sidecar's hello rather than off the token this tab happens to hold, so a tab
   * can display what the SIGNATURE bought it rather than what its own unverified claims say.
   */
  readonly onBound?: (playerUuid: UuidText) => void;
  /**
   * Any server message the channel did not consume itself. THE SEAM FOR PHASE 2: an `alert` lands
   * here with no change to this file, because `isAudioServerMessage` drops types it does not know
   * and this callback gets everything that survived it.
   */
  readonly onMessage?: (message: AudioServerMessage) => void;
  /**
   * THE STALE TAB'S ONLY JOB. The sidecar refused this tab's protocol generation, which means the
   * page is running code older (or newer) than the deployment. There is nothing to retry: reload.
   *
   * A caller shows a "a new version is available — reload" banner. Calling `location.reload()`
   * outright would throw away whatever the person was doing in another part of the SPA, so the
   * decision is the caller's and the channel just stops.
   */
  readonly onReloadRequired?: (detail: string) => void;
  /** The credential provider refused: there is no audio session in this browser any more. */
  readonly onCredentialRefused?: (detail: string) => void;
}

/**
 * The credential provider said there is no credential to be had — the renewal is gone (signed out,
 * expired, or the key rotated). Nothing a retry can fix: the person has to type `/audio` again.
 */
export class AudioCredentialRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AudioCredentialRefused';
  }
}

export interface AudioChannelOptions {
  /** Origin of the sidecar, e.g. `https://api.example.com`; empty for same-origin. The path is appended. */
  readonly apiBase: string;
  /**
   * A fixed credential — a player-tier session token. Kept for the case where the caller already holds
   * one for exactly as long as the channel will live (tests, a short-lived page).
   */
  readonly token?: string;
  /**
   * A FRESH credential before every dial — the closed beta's way (plan §9.2.4). A tab lives for hours
   * and a session for fifteen minutes, so the page mints a two-minute socket ticket from its renewal
   * cookie each time it dials, and a reconnect after an hour presents a ticket minted an instant ago.
   * Rejecting with {@link AudioCredentialRefused} stops the channel; any other rejection is a network
   * fault and is retried on the usual backoff.
   */
  readonly credential?: () => Promise<string>;
  readonly events?: AudioChannelEvents;
  /** Injected by tests. Defaults to the real `WebSocket`. */
  readonly socketFactory?: (url: string, protocols: string[]) => AudioSocketLike;
  /** Injected by tests. Epoch milliseconds. */
  readonly now?: () => number;
  /** Injected by tests. Uniform in [0,1). */
  readonly random?: () => number;
}

/**
 * One socket, one player, one clock.
 *
 * THE CREDENTIAL IS HANDED IN AND NOT READ FROM STORAGE. The channel does not decide who it is; the
 * caller that owns the session hands over either a token or a provider that mints one per dial (the
 * audio page's provider asks `POST /v1/audio/session`, which reads an `httpOnly` renewal cookie this
 * file never sees). That keeps this file out of the auth story entirely — it cannot pick up a stale
 * token, cannot refresh one by itself, and cannot outlive a sign-out the session owner performed.
 *
 * WHAT THE TAB MAY CONCLUDE FROM A CONNECTED SOCKET: which player it is bound to, and only because
 * the sidecar said so in the hello. Editing the token in devtools produces a refused handshake, not
 * a different stream; the uuid comes off the signature the sidecar verified.
 */
export class AudioChannel {
  private readonly options: AudioChannelOptions;
  private readonly events: AudioChannelEvents;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly newSocket: (url: string, protocols: string[]) => AudioSocketLike;

  readonly clock = new AudioClock();

  private socket: AudioSocketLike | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private probeTimer: ReturnType<typeof setInterval> | null = null;
  private currentStatus: AudioChannelStatus = 'idle';
  private attempt = 0;
  private nextProbeId = 1;
  private boundTo: UuidText | null = null;
  /** When the current dial started — the hello's T0. See where it is used. */
  private dialAtMs = 0;

  /**
   * Probes we have sent and not yet had answered: id → the local instant we sent it.
   *
   * BOUNDED. Pruned on every probe, and a probe older than a few intervals is dropped rather than
   * kept in the hope of a very late pong — a stale T0 would give a confidently wrong offset. A tab
   * left open for a week holds at most a handful of entries, and the clock itself holds nine
   * samples whatever happens (see `AUDIO_CLOCK_WINDOW`).
   */
  private readonly pending = new Map<number, number>();

  constructor(options: AudioChannelOptions) {
    this.options = options;
    this.events = options.events ?? {};
    this.now = options.now ?? (() => Date.now());
    this.random = options.random ?? Math.random;
    this.newSocket =
      options.socketFactory ??
      ((url, protocols) => new WebSocket(url, protocols) as unknown as AudioSocketLike);
  }

  get status(): AudioChannelStatus {
    return this.currentStatus;
  }

  /** `serverClock - localClock`, signed. Zero until the first sample. See `AudioClock`. */
  get offsetMs(): number {
    return this.clock.offsetMs;
  }

  /** The player this socket is bound to, per the sidecar's hello. Null until one arrives. */
  get boundPlayer(): UuidText | null {
    return this.boundTo;
  }

  /** How many consecutive failures there have been. Zero while open. */
  get attempts(): number {
    return this.attempt;
  }

  /**
   * The `wss://…/v1/audio` URL this channel dials. `http(s)` in `apiBase` becomes `ws(s)`; an empty
   * base is same-origin, which is the page's own host with the page's own scheme.
   */
  get url(): string {
    const base = this.options.apiBase.replace(/\/+$/, '');
    const origin = base === '' && typeof globalThis.location !== 'undefined' ? globalThis.location.origin : base;
    return `${origin.replace(/^http/, 'ws')}${AUDIO_PATH}`;
  }

  /** Opens the socket. Idempotent: calling it while connected or waiting does nothing. */
  start(): void {
    // `stopped` is in this list on purpose: see stop(). Every reason a channel stops is a reason a
    // fresh one would need a fresh token, so restarting this object is not offered.
    if (this.currentStatus !== 'idle') return;
    this.attempt = 0;
    this.dial();
  }

  /**
   * Closes for good and releases every timer. A channel that has been stopped is not restarted —
   * `start()` after `stop()` is a no-op, so a caller that wants a new socket builds a new channel.
   * That is deliberate: the reasons a channel stops (a refused tier, a stale protocol, a sign-out)
   * are all reasons a fresh one would need a fresh token anyway.
   */
  stop(): void {
    this.clearTimers();
    const socket = this.socket;
    this.socket = null;
    this.pending.clear();
    this.clock.reset();
    if (socket !== null) {
      this.detach(socket);
      try {
        socket.close(1000, 'stopped');
      } catch {
        /* already gone */
      }
    }
    this.setStatus('stopped');
  }

  // ------------------------------------------------------------------ internals

  private dial(): void {
    const provider = this.options.credential;
    if (provider === undefined) {
      this.dialWith(this.options.token ?? '');
      return;
    }
    this.setStatus('connecting');
    const attempt = this.attempt;
    provider().then(
      (credential) => {
        // Stopped, or superseded by another dial, while the credential was being minted.
        if (this.currentStatus !== 'connecting' || this.attempt !== attempt || this.socket !== null) return;
        this.dialWith(credential);
      },
      (error: unknown) => {
        if (this.currentStatus !== 'connecting') return;
        if (error instanceof AudioCredentialRefused) {
          this.stopWith(error.message);
          this.events.onCredentialRefused?.(error.message);
          return;
        }
        this.scheduleRetry(`could not get a socket credential: ${error instanceof Error ? error.message : String(error)}`);
      },
    );
  }

  private dialWith(credential: string): void {
    this.dialAtMs = this.now();
    this.setStatus('connecting');
    /*
     * THE CREDENTIAL TRAVELS AS A SUBPROTOCOL, because a browser's `WebSocket` constructor takes a
     * URL and a protocol list and nothing else — it cannot set an `Authorization` header. The
     * alternatives were a query parameter (which lands in every access log, `Referer` and proxy
     * trace — the same reason the login token arrives in the URL *fragment*) or a login frame after
     * the socket is already open (which means an unauthenticated socket exists, however briefly,
     * and needs its own timeout and its own cap). See `web/shared/src/audio.ts`.
     */
    const protocols = [audioVersionSubprotocol(), audioTokenSubprotocol(credential)];

    let socket: AudioSocketLike;
    try {
      socket = this.newSocket(this.url, protocols);
    } catch (error) {
      // A malformed URL, or a browser refusing the scheme. Not retryable by itself, but treating it
      // as a normal failure means one code path and a bounded, jittered retry either way.
      this.scheduleRetry(`could not open a socket: ${error instanceof Error ? error.message : String(error)}`);
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      // NOT `open` yet: the hello has not arrived, so nothing about this socket is known and nothing
      // may be sent. `onmessage` promotes it once it has validated one.
      this.pending.clear();
    };
    socket.onmessage = (event) => this.onFrame(socket, event.data);
    socket.onerror = () => {
      // The browser deliberately tells a page nothing about why a socket failed (it would be a
      // cross-origin probe). `onclose` follows and carries the code, so this only has to not throw.
    };
    socket.onclose = (event) => this.onClose(socket, event.code, event.reason);
  }

  private onFrame(socket: AudioSocketLike, data: unknown): void {
    if (socket !== this.socket) return; // a frame from a socket we have already replaced
    if (typeof data !== 'string') return; // nothing in this protocol is binary
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return; // a proxy error page, or a sidecar bug. Dropping it is the whole response.
    }
    if (!isAudioServerMessage(parsed)) return; // includes every type a newer sidecar may add

    switch (parsed.type) {
      case 'hello': {
        if (parsed.playerUuid === undefined) {
          // A `server`-peer hello on a browser socket. The sidecar took this tab's session token for
          // the shared server secret, which cannot happen — and if it did, a tab that carried on
          // would be listening to a stream that is not one player's.
          this.stopWith('the sidecar did not bind this socket to a player');
          return;
        }
        this.attempt = 0;
        this.boundTo = parsed.playerUuid;
        /*
         * THE HELLO IS THE FIRST CLOCK SAMPLE, FOR FREE - so the offset is useful immediately rather
         * than zero until the first probe returns.
         *
         * T0 is the instant the DIAL started, not `now()` twice. Two reasons, and the second is the
         * one that bites: a fabricated zero round trip is a lie about precision, and it also becomes
         * the window's fastest sample, which is what the outlier filter measures every other probe
         * against (see AUDIO_CLOCK_RTT_TOLERANCE_MS). The handshake is a real round trip and it is
         * the only measurement this sample has.
         */
        this.clock.add({ sentAtMs: this.dialAtMs, serverTimeMs: parsed.serverTimeMs, receivedAtMs: this.now() });
        this.setStatus('open');
        this.events.onBound?.(parsed.playerUuid);
        this.startProbing(parsed.pingIntervalMs > 0 ? parsed.pingIntervalMs : AUDIO_PING_INTERVAL_MS);
        return;
      }
      case 'pong': {
        const sentAtMs = this.pending.get(parsed.id);
        this.pending.delete(parsed.id);
        // A pong for a probe we have forgotten (pruned as too old, or a duplicate) is dropped:
        // feeding it would mean inventing a T0, and an invented T0 is a wrong offset presented with
        // exactly the same confidence as a right one.
        if (sentAtMs !== undefined) {
          this.clock.add({ sentAtMs, serverTimeMs: parsed.serverTimeMs, receivedAtMs: this.now() });
        }
        return;
      }
      case 'refused': {
        this.onRefused(parsed.code, parsed.detail ?? parsed.code);
        return;
      }
      default:
        this.events.onMessage?.(parsed);
    }
  }

  /**
   * The sidecar named a reason. The close frame follows on its own; this decides whether there is
   * any point reconnecting when it does.
   */
  private onRefused(code: AudioRefusalCode, detail: string): void {
    if (code === 'protocol_version') {
      // The one refusal whose remedy is neither a retry nor a sign-in.
      this.stopWith(detail);
      this.events.onReloadRequired?.(detail);
      return;
    }
    if (code === 'unauthorised' || code === 'forbidden_tier') {
      // Reconnecting with the same token produces the same refusal forever. The session owner has
      // to do something (sign in again, or run `/link`), so the channel stops and says why.
      this.stopWith(detail);
      return;
    }
    // `superseded`, `too_many_sockets`, `going_away`, `bad_frame`: the close handler retries.
  }

  private onClose(socket: AudioSocketLike, code: number, reason: string): void {
    if (socket !== this.socket) return;
    if (this.currentStatus === 'stopped') return; // a refusal already decided this
    this.detach(socket);
    this.socket = null;
    this.stopProbing();
    this.pending.clear();
    // The samples described a socket that no longer exists; keeping them would average a new
    // network against an old one.
    this.clock.reset();
    this.boundTo = null;
    this.scheduleRetry(`the socket closed (${code}${reason.length > 0 ? ` ${reason}` : ''})`);
  }

  private scheduleRetry(why: string): void {
    this.clearTimers();
    const delay = audioRetryDelayMs(this.attempt, this.random);
    this.attempt += 1;
    this.setStatus('waiting', `${why}; reconnecting in ${Math.round(delay / 100) / 10} s`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.dial();
    }, delay);
  }

  private startProbing(intervalMs: number): void {
    this.stopProbing();
    this.probe();
    this.probeTimer = setInterval(() => this.probe(), intervalMs);
  }

  private stopProbing(): void {
    if (this.probeTimer !== null) {
      clearInterval(this.probeTimer);
      this.probeTimer = null;
    }
  }

  private probe(): void {
    const socket = this.socket;
    if (socket === null || this.currentStatus !== 'open') return;
    const id = this.nextProbeId++;
    const clientTimeMs = this.now();
    this.prunePending(clientTimeMs);
    this.pending.set(id, clientTimeMs);
    try {
      socket.send(JSON.stringify({ type: 'ping', id, clientTimeMs }));
    } catch {
      // A send that throws is a socket that is gone; `onclose` will follow and schedule the retry.
      this.pending.delete(id);
    }
  }

  /** Drops probes too old to be worth an answer. See {@link pending}. */
  private prunePending(nowMs: number): void {
    const horizon = AUDIO_PING_INTERVAL_MS * 4;
    for (const [id, sentAtMs] of this.pending) {
      if (nowMs - sentAtMs > horizon) this.pending.delete(id);
    }
  }

  private stopWith(detail: string): void {
    this.clearTimers();
    this.stopProbing();
    const socket = this.socket;
    this.socket = null;
    this.pending.clear();
    this.clock.reset();
    this.boundTo = null;
    if (socket !== null) {
      this.detach(socket);
      try {
        socket.close(1000, 'stopped');
      } catch {
        /* already gone */
      }
    }
    this.setStatus('stopped', detail);
  }

  private clearTimers(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.stopProbing();
  }

  /** Unhooks every handler, so a socket we have given up on cannot report anything later. */
  private detach(socket: AudioSocketLike): void {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
  }

  private setStatus(status: AudioChannelStatus, detail?: string): void {
    this.currentStatus = status;
    this.events.onStatus?.(status, detail);
  }
}
